<script setup lang="ts">
	import * as z from 'zod'
	import type { FormSubmitEvent } from '@nuxt/ui'

	const rConf = useRuntimeConfig()

	const { location } = defineProps<{
		location?: object
	}>()

	const schema = z.object({
		location: z.string().optional(),
		name: z.string().nonempty('Sie müssen ihren Namen angeben').min(5, 'Name muss mindestens 5 Buchstaben lang sein'),
		email: z.email({error: 'Bitte eine gültige E-Mail-Adresse angeben'}),
		message: z.string().nonempty('Sie müssen eine Nachricht formulieren').min(15, 'Ihre Nachricht muss mindestens 15 Zeichen lang sein').max(1000, 'Nachrichten sind auf 1000 Zeichen beschränkt'),
		privacy: z.boolean({required_error: 'Sie müssen unsere geltenden Datenschutzbestimmungen akzeptieren'})
	})

	type Schema = z.output<typeof schema>

	const state = reactive<Partial<Schema>>({
		location: location ? location.name : undefined,
		name: '',
		email: '',
		message: '',
		privacy: false
	})

	const loading = ref(false)
	const showError = ref<boolean>(false)
	const showSuccess = ref<boolean>(false)

	async function onSubmit(event: FormSubmitEvent<Schema>) {
		loading.value = true
		try {
			const body: any = { data: JSON.parse(JSON.stringify(event.data)), honey: true, hook_id: '01KESERKKPP2KV27WKG6XSM41Q' }
			await $fetch('/rest/cms/mailing', { method: 'POST', body, baseURL: rConf.public.apiBase })
			showSuccess.value = true
			setTimeout(() => showSuccess.value = false, 6000)
		} catch (e: any) {
		 	showError.value = true
		 	setTimeout(() => showError.value = false, 6000)
		} finally {
			loading.value = false
			state.name = ''
			state.email = ''
			state.message = ''
			state.privacy = false
		}
	}
</script>

<template>
	<div>
		<template v-if="showSuccess || showError">
			<div class="w-full h-full flex my-8">
				<span v-if="showSuccess" class="w-[90%] lg:w-[80%] m-auto text-2xl font-medium text-accent text-center">
					Vielen Dank für Ihre Anfrage. Wir setzen uns so schnell wie möglich mit Ihnen in Verbindung.
				</span>
				<span v-if="showError" class="w-[90%] lg:w-[80%] m-auto text-2xl font-medium text-red-500 text-center">Etwas ist
					schief gelaufen. Bitte versuchen Sie es noch einmal oder kontaktieren Sie uns über Telefon oder
					E-Mail.
				</span>
			</div>
		</template>
		<UForm :schema="schema" :state="state" class="space-y-4 lg:space-y-8" @submit="onSubmit">
			<UFormField label="Name" name="name" class="[&_label]:font-manrope [&_label]:text-woodsmoke-950 [&_label]:text-[1rem] md:[&_label]:text-[1.25rem] [&_label]:font-medium">
				<UInput v-model="state.name" required placeholder="Maxi Musterfrau" size="xl" class="w-full" />
			</UFormField>

			<UFormField label="E-Mail" name="email" class="[&_label]:font-manrope [&_label]:text-woodsmoke-950 [&_label]:text-[1rem] md:[&_label]:text-[1.25rem] [&_label]:font-medium">
				<UInput v-model="state.email" placeholder="maxi@musterfrau.de" required size="xl" class="w-full" />
			</UFormField>

			<UFormField label="Nachricht" name="message" class="[&_label]:font-manrope [&_label]:text-woodsmoke-950 [&_label]:text-[1rem] md:[&_label]:text-[1.25rem] [&_label]:font-medium">
			 	<UTextarea v-model="state.message" required :rows="12" placeholder="Ihre Nachricht an uns" size="xl" class="w-full" />
			</UFormField>

			<UFormField name="privacy">
				<UCheckbox v-model="state.privacy" required size="xl" variant="list" class="gap-2">
					<template #label>Ich habe die Informationen zur <NuxtLink :to="{ name: 'article-detail', params: { article_slug: 'datenschutzerklaerung'}}" target="_blank">Datenverarbeitung</NuxtLink> zur Kenntnis genommen.</template>
				</UCheckbox>
			</UFormField>

			<div class="w-full flex justify-start lg:justify-end">
				<UButton type="submit" :loading="loading" title="Formular absenden" class="mt-4 cursor-pointer">
					<span>Absenden</span>
				</UButton>
			</div>
		</UForm>
	</div>
</template>

<style>
	.text-error {
		font-size: 14px;
	}
</style>
